import React from "react";
import { motion } from "framer-motion";
import { useTr } from "@/autoTranslate";
import { useSiteContent } from "@/siteContent";

const FALLBACK = ["var(--cobalt)", "var(--coral)", "var(--chartreuse)"];

export default function Skills() {
  const tr = useTr();
  const site = useSiteContent();
  const groups = site.getList("skills.groups");
  const langs = site.getList("languages.list");
  const education = site.getList("education.list");

  return (
    <section
      id="skills"
      data-testid="skills-section"
      className="relative px-6 md:px-10 py-8 md:py-12"
    >
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="display-sans text-6xl md:text-[10vw] leading-[0.9]"
      >
        {tr(site.get("skills.title"))}
      </motion.h2>

      <div className="mt-16 md:mt-28 grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-24">
        {groups.map((g, gi) => {
          const color = g.color || FALLBACK[gi % FALLBACK.length];
          return (
            <div key={gi} data-testid={`skill-group-${gi}`}>
              <div className="flex items-center gap-3 pb-4 border-b border-[color:var(--ink)]">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: color }}
                />
                <span className="font-mono-label text-[11px] uppercase tracking-[0.18em]">
                  {tr(g.label)}
                </span>
              </div>
              <ul className="mt-2">
                {(g.items || []).map((name, i) => (
                  <li
                    key={i}
                    className="group py-4 border-b border-[color:var(--hair)] flex items-center gap-4"
                  >
                    <span
                      className="inline-block w-4 h-px shrink-0 transition-all duration-300 group-hover:w-7"
                      style={{ background: color }}
                    />
                    <span className="text-lg md:text-xl leading-snug">
                      {tr(name)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Languages + Education */}
      <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-6">
          <div className="font-mono-label text-[11px] uppercase tracking-widest opacity-80">
            {tr(site.get("languages.title"))}
          </div>
          <ul className="mt-6 divide-y divide-[color:var(--hair)] border-y border-[color:var(--hair)]">
            {langs.map((l, i) => (
              <li
                key={i}
                className="py-5 flex items-baseline justify-between gap-6"
              >
                <span className="display-serif italic text-3xl md:text-5xl">
                  {tr(l.name)}
                </span>
                <span className="font-mono-label text-[11px] uppercase tracking-widest opacity-70">
                  {tr(l.level)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-6">
          <div className="font-mono-label text-[11px] uppercase tracking-widest opacity-80">
            {tr(site.get("education.title"))}
          </div>
          {education.map((ed, i) => (
            <div
              key={i}
              className="mt-6 border-t border-b border-[color:var(--hair)] py-6"
            >
              <div className="flex items-baseline justify-between font-mono-label text-[11px] uppercase tracking-widest opacity-70">
                <span>{tr(ed.school)}</span>
                <span>{ed.period}</span>
              </div>
              <h4 className="mt-3 display-serif italic text-3xl md:text-5xl">
                {tr(ed.degree)}
              </h4>
              {ed.note && (
                <p className="mt-3 text-base md:text-lg opacity-80 max-w-lg">
                  {tr(ed.note)}
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-2">
                {(ed.tags || []).map((t, ti) => (
                  <span key={ti} className="sticker">
                    {tr(t)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
